import { Box, Card, CardContent, CircularProgress, Stack, Tooltip, Typography } from "@mui/material"
import { useEffect, useState } from "react"
import { DiaryEntryListModelPagedResponseModel, UserActivityListModel, UserFeelingListModel } from "services/openapi"
import useApiRequestError from "utils/hooks/useApiRequestErrorHandling"
import { orderBy, union } from "lodash/fp"
import { getFeelingUser } from "utils/apiRequests/fetchFeelings"
import { getActivityEntries, getDiaryEntries } from "scenes/CareOrganizationUserClientDetails/apiRequests"
import { toFinnishFormat } from "utils/functions/toDateFormat"
import { Timespan } from "components/TimeSpanSelectionButtons"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import InfoIcon from "@mui/icons-material/Info"
import { useTranslation } from "react-i18next"

type UserDayListingProps = {
  clientId: string
  timespan: Timespan
  dayClicked: (date: string) => void
}

const UserDayListing = ({ clientId, timespan, dayClicked }: UserDayListingProps) => {
  const [isLoadingFeelings, setLoadingFeelings] = useState<boolean>(true)
  const [isLoadingDiaryEntries, setLoadingDiaryEntries] = useState<boolean>(true)
  const [isLoadingActivities, setLoadingActivities] = useState<boolean>(true)
  const [feelings, setFeelings] = useState<UserFeelingListModel[]>([])
  const [activities, setActivities] = useState<UserActivityListModel[]>([])
  const [diaryEntries, setDiaryEntries] = useState<DiaryEntryListModelPagedResponseModel>({
    items: [],
    page: 0,
    pageSize: undefined,
    total: undefined,
  })
  const { ErrorSnackbar, handleError } = useApiRequestError()
  const { t } = useTranslation()

  useEffect(
    function fetchData() {
      getFeelingUser(clientId, timespan.start, timespan.end, setLoadingFeelings, setFeelings, handleError)
      getDiaryEntries(clientId, timespan.start, timespan.end, setLoadingDiaryEntries, setDiaryEntries, handleError)
      getActivityEntries(clientId, timespan.start, timespan.end, setLoadingActivities, setActivities, handleError)
    },
    [clientId, timespan.start, timespan.end, handleError]
  )

  const feelingDates = feelings.map((f) => f.date.split("T")[0])
  const diaryDates = diaryEntries.items.map((d) => d.date.split("T")[0])
  const activityDates = activities.map((d) => d.date.split("T")[0])
  const unionOfDates = union(union(feelingDates, diaryDates), activityDates)
  const sortedDates = orderBy((date) => date, "desc", unionOfDates)

  const weekDayKeys = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]

  const cards = sortedDates.map((date) => {
    const weekDayKey = new Date(date).getDay()
    const weekDayTranslationKey = `weekdays.long.${weekDayKeys[weekDayKey]}`

    return (
      <Card
        key={date}
        sx={{
          borderRadius: 3,
          mt: 1,
          mx: 2,
        }}
      >
        <CardContent>
          <Box
            sx={{ cursor: "pointer" }}
            onClick={() => {
              dayClicked(date)
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontWeight: "bold", pb: 1 }}>
                  {t(weekDayTranslationKey)} {toFinnishFormat(date)}
                </Typography>
              </Stack>
              <OpenInNewIcon fontSize="large" sx={{ color: "text.secondary", pb: 1 }} />
            </Stack>
            <ErrorSnackbar />
          </Box>
        </CardContent>
      </Card>
    )
  })

  return (
    <>
      {isLoadingFeelings || isLoadingDiaryEntries || isLoadingActivities ? (
        <CircularProgress size={100} />
      ) : (
        <Box
          sx={{
            pt: 1,
            pb: 12,
          }}
        >
          <Stack direction="row" alignItems="center">
            <Typography variant="h6" sx={{ mt: 2, mx: 2 }}>
              {t("common.diary-entries")}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Tooltip
                title={t("common.diary-entries-tooltip") as string}
                enterTouchDelay={0}
                leaveTouchDelay={3000}
                disableInteractive
              >
                <InfoIcon color="info" sx={{ cursor: "pointer" }} />
              </Tooltip>
            </Box>
          </Stack>
          {cards}
        </Box>
      )}
      <ErrorSnackbar />
    </>
  )
}

export default UserDayListing
